import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Quiz, QuizAnswer, QuizQuestion } from '../helpers/quizHelper';

const SAVE_DELAY_MS = 500;

type Selections = Record<string, number>;
type Revealed = Record<string, boolean>;

/**
 * One quiz's live state, shared by the chat card and the side panel.
 *
 * Answers are local while you work, autosaved shortly after each change and
 * graded on the server. Whenever nothing is waiting to save, the server's
 * answers are adopted, so a card and a panel open on the same quiz (or another
 * device) stay in step.
 */
export function useQuizSession(quizId: string | undefined, { readOnly = false } = {}) {
    const auth = useAuth();
    const toast = useToast();
    const userId = auth.user?._id ? String(auth.user._id) : undefined;
    const legacyUserId = auth.user?.legacyId;

    const quiz: Quiz | null | undefined = useQuery(
        "quizzes:get" as any,
        quizId && userId ? { quizId, userId, legacyUserId } : "skip"
    );
    const saveAnswers = useMutation("quizzes:saveAnswers" as any);
    const submitQuiz = useMutation("quizzes:submit" as any);
    const startRetake = useMutation("quizzes:startRetake" as any);

    const [selections, setSelections] = useState<Selections>({});
    const [revealed, setRevealed] = useState<Revealed>({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectionsRef = useRef(selections);
    const revealedRef = useRef(revealed);
    const latestQuiz = useRef(quiz);
    selectionsRef.current = selections;
    revealedRef.current = revealed;
    latestQuiz.current = quiz;

    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inFlight = useRef(0);
    const loadedAttemptId = useRef<string | null | undefined>(undefined);

    const questions: QuizQuestion[] = useMemo(() => quiz?.questions ?? [], [quiz]);
    const attempt = quiz?.attempt ?? null;
    const isGraded = attempt?.submittedAt !== undefined && attempt?.submittedAt !== null;
    const answeredCount = questions.filter(q => selections[q.id] !== undefined).length;
    const canSubmit = questions.length > 0 && answeredCount === questions.length && !isGraded && !isSubmitting && !readOnly;

    const applyServerAnswers = useCallback((serverQuiz: Quiz) => {
        const nextSelections: Selections = {};
        const nextRevealed: Revealed = {};
        for (const answer of serverQuiz.attempt?.answers ?? []) {
            if (answer.choiceIndex !== null) nextSelections[answer.questionId] = answer.choiceIndex;
            if (answer.revealed) nextRevealed[answer.questionId] = true;
        }
        setSelections(nextSelections);
        setRevealed(nextRevealed);

        // A new attempt (first load or a retake) resumes at the first
        // question that hasn't been answered or revealed.
        const attemptId = serverQuiz.attempt?._id ?? null;
        if (attemptId !== loadedAttemptId.current) {
            loadedAttemptId.current = attemptId;
            const next = serverQuiz.questions.findIndex(q => nextSelections[q.id] === undefined && !nextRevealed[q.id]);
            setCurrentIndex(next === -1 ? 0 : next);
        }
    }, []);

    const syncIfIdle = useCallback(() => {
        if (saveTimer.current || inFlight.current > 0 || !latestQuiz.current) return;
        applyServerAnswers(latestQuiz.current);
    }, [applyServerAnswers]);

    useEffect(() => { syncIfIdle(); }, [quiz, syncIfIdle]);

    const buildAnswers = useCallback((): QuizAnswer[] =>
        (latestQuiz.current?.questions ?? []).map(q => ({
            questionId: q.id,
            choiceIndex: selectionsRef.current[q.id] ?? null,
            revealed: !!revealedRef.current[q.id],
        })), []);

    const flushSave = useCallback(() => {
        saveTimer.current = null;
        if (readOnly || !quizId || !userId) return;
        inFlight.current += 1;
        saveAnswers({ quizId, userId, legacyUserId, answers: buildAnswers() })
            .catch(error => console.error('Failed to save quiz answers:', error))
            .finally(() => {
                inFlight.current -= 1;
                syncIfIdle();
            });
    }, [readOnly, quizId, userId, legacyUserId, saveAnswers, buildAnswers, syncIfIdle]);

    const scheduleSave = useCallback(() => {
        if (readOnly) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(flushSave, SAVE_DELAY_MS);
    }, [readOnly, flushSave]);

    // Send a pending autosave rather than dropping it on unmount.
    const flushRef = useRef(flushSave);
    flushRef.current = flushSave;
    useEffect(() => () => {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
            flushRef.current();
        }
    }, []);

    const isLocked = useCallback(
        (question: QuizQuestion) => isGraded || readOnly || !!revealed[question.id],
        [isGraded, readOnly, revealed]
    );

    const select = useCallback((question: QuizQuestion, choiceIndex: number) => {
        if (isLocked(question) || selectionsRef.current[question.id] === choiceIndex) return;
        const next = { ...selectionsRef.current, [question.id]: choiceIndex };
        selectionsRef.current = next;
        setSelections(next);
        scheduleSave();
    }, [isLocked, scheduleSave]);

    const reveal = useCallback((question: QuizQuestion) => {
        if (isLocked(question)) return;
        const next = { ...revealedRef.current, [question.id]: true };
        revealedRef.current = next;
        setRevealed(next);
        scheduleSave();
    }, [isLocked, scheduleSave]);

    const submit = useCallback(async () => {
        if (!canSubmit || !quizId || !userId) return;
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
            saveTimer.current = null;
        }
        setIsSubmitting(true);
        inFlight.current += 1;
        try {
            // Saves and grades in one transaction.
            await submitQuiz({ quizId, userId, legacyUserId, answers: buildAnswers() });
        } catch (error) {
            console.error('Failed to grade quiz:', error);
            toast('Failed to Grade Quiz', 'error');
        } finally {
            inFlight.current -= 1;
            setIsSubmitting(false);
            syncIfIdle();
        }
    }, [canSubmit, quizId, userId, legacyUserId, submitQuiz, buildAnswers, toast, syncIfIdle]);

    const retake = useCallback(async () => {
        if (!quizId || !userId || readOnly) return;
        try {
            await startRetake({ quizId, userId, legacyUserId });
        } catch (error) {
            console.error('Failed to start quiz retake:', error);
            toast('Failed to Start Retake', 'error');
        }
    }, [quizId, userId, legacyUserId, readOnly, startRetake, toast]);

    const safeIndex = Math.min(currentIndex, Math.max(questions.length - 1, 0));

    return {
        quiz,
        isLoading: quiz === undefined && !!quizId && !!userId,
        questions,
        selections,
        revealed,
        isGraded,
        isSubmitting,
        answeredCount,
        canSubmit,
        score: attempt?.score ?? 0,
        currentIndex: safeIndex,
        currentQuestion: questions[safeIndex] as QuizQuestion | undefined,
        isOnLastQuestion: safeIndex >= questions.length - 1,
        goToPrevious: () => setCurrentIndex(Math.max(safeIndex - 1, 0)),
        goToNext: () => setCurrentIndex(Math.min(safeIndex + 1, Math.max(questions.length - 1, 0))),
        isLocked,
        select,
        reveal,
        submit,
        retake,
    };
}

export type QuizSession = ReturnType<typeof useQuizSession>;
